/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * Palette: "Gold + Teal + Cream" - Matching MyTrustie Website Branding
 *  - Gold (#e8b53b) is the primary brand color for CTAs, active nav, and highlights
 *  - Teal (#3a8aa3) is the secondary structural color for links and accents
 *  - Teal Deep (#1f3d4a) is used for dark text and backgrounds
 *  - Cream (#fdfcf9) is the light background color
 */

const colors = {
  background: {
    default: "#fdfcf9",
  },

  text: {
    main: "#1a2a35",
    focus: "#0f1417",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  cream: {
    main: "#fff3cf",
    focus: "#ffe6b8",
  },

  black: {
    light: "#1a2a35",
    main: "#0f1417",
    focus: "#000000",
  },

  primary: {
    main: "#e8b53b",
    focus: "#c99d2d",
  },

  secondary: {
    main: "#1a2a35",
    focus: "#0f1417",
  },

  info: {
    main: "#3a8aa3",
    focus: "#2d6f85",
  },

  success: {
    main: "#6baa7d",
    focus: "#4f8961",
  },

  warning: {
    main: "#e8b53b",
    focus: "#c99d2d",
  },

  error: {
    main: "#d32f2f",
    focus: "#b71c1c",
  },

  light: {
    main: "#fdf8ee",
    focus: "#fff1d1",
  },

  dark: {
    main: "#1f3d4a",
    focus: "#0f1417",
  },

  grey: {
    100: "#fdf8ee",
    200: "#f5e8d6",
    300: "#dce0cc",
    400: "#a89f8c",
    500: "#74705f",
    600: "#555243",
    700: "#3a3830",
    800: "#252420",
    900: "#1a2a35",
  },

  gradients: {
    // Gold → light gold — primary CTA button sheen.
    primary: {
      main: "#e8b53b",
      state: "#f5e6b8",
    },

    secondary: {
      main: "#1a2a35",
      state: "#0f1417",
    },

    info: {
      main: "#3a8aa3",
      state: "#5ba6c4",
    },

    success: {
      main: "#6baa7d",
      state: "#a6c9b1",
    },

    // Gold → light gold — hero base + warm CTAs.
    warning: {
      main: "#e8b53b",
      state: "#f5e6b8",
    },

    error: {
      main: "#d32f2f",
      state: "#e57373",
    },

    light: {
      main: "#fdf8ee",
      state: "#fffbf0",
    },

    dark: {
      main: "#1f3d4a",
      state: "#0f1417",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  alertColors: {
    primary: {
      main: "#e8b53b",
      state: "#f5e6b8",
      border: "#fce8d6",
    },

    secondary: {
      main: "#1a2a35",
      state: "#0f1417",
      border: "#3a3830",
    },

    info: {
      main: "#3a8aa3",
      state: "#5ba6c4",
      border: "#d6e2ec",
    },

    success: {
      main: "#6baa7d",
      state: "#a6c9b1",
      border: "#d6e7da",
    },

    warning: {
      main: "#e8b53b",
      state: "#f5e6b8",
      border: "#fff3cf",
    },

    error: {
      main: "#d32f2f",
      state: "#e57373",
      border: "#f9d6df",
    },

    light: {
      main: "#fdf8ee",
      state: "#fffbf0",
      border: "#f5e8d6",
    },

    dark: {
      main: "#1f3d4a",
      state: "#0f1417",
      border: "#3a3830",
    },
  },

  badgeColors: {
    primary: {
      background: "#fce8d6",
      text: "#8a6d2b",
    },

    secondary: {
      background: "#dcdfe4",
      text: "#1a2a35",
    },

    info: {
      background: "#d6e2ec",
      text: "#2d6f85",
    },

    success: {
      background: "#d6e7da",
      text: "#3b6a4a",
    },

    warning: {
      background: "#fff3cf",
      text: "#9c7a1f",
    },

    error: {
      background: "#f9d6df",
      text: "#a83a5a",
    },

    light: {
      background: "#fdf8ee",
      text: "#1a2a35",
    },

    dark: {
      background: "#1f3d4a",
      text: "#ffffff",
    },
  },

  inputColors: {
    borderColor: { main: "#d6cfbc", focus: "#e8b53b" },
    boxShadow: "#f5e6b8",
    error: "#d32f2f",
    success: "#6baa7d",
  },

  sliderColors: {
    thumb: { borderColor: "#d9d9d9" },
  },

  circleSliderColors: {
    background: "#d3d3d3",
  },

  tabs: {
    indicator: { boxShadow: "#e8b53b" },
  },
};

export default colors;